import { TrajectoriesChart } from "charts/TrajectoriesChart";
import { useReadScrolly } from "components/scrolly";
import { format } from "d3-format";
import { useCallback } from "react";
import { defineMessage, useIntl } from "react-intl";
import { withFigureIO } from "../components/figure";
import { metadata } from "../data/data_learning_trajectories_fig_TRAJECTORIES_SIMULATION_EQUALITY";
import { datasets } from "../data/data_learning_trajectories_fig_TRAJECTORIES_SIMULATION_EQUALITY_datasets";
import { goalRule, RiseDataDecoder, xAxisTitleLookup, yAxisTitleLookup } from "../domain";
import { useMedia } from "../hooks";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { E, io, O, pipe } from "../prelude";
export * from "../data/data_learning_trajectories_fig_TRAJECTORIES_SIMULATION_EQUALITY";
// Scrollytelling steps
const Steps = io.union([
    io.literal("CURRENT"),
    io.literal("SIMULATION_ACCESS"),
    io.literal("SIMULATION_LEARNING"),
    io.literal("COMPARISON"),
    io.literal("COMPARISON_GROUP"),
    io.literal("default")
]);
// Translation lookups
const seriesLabelsLookup = {
    CURRENT: defineMessage({
        id: "fig.learning.trajectories.TRAJECTORIES_SIMULATION_EQUALITY.label.current",
        defaultMessage: "Trajectory of the poor"
    }),
    SIMULATION_ACCESS: defineMessage({
        id: "fig.learning.trajectories.TRAJECTORIES_SIMULATION_EQUALITY.label.simulationAccess",
        defaultMessage: "Equal access simulation"
    }),
    SIMULATION_LEARNING: defineMessage({
        id: "fig.learning.trajectories.TRAJECTORIES_SIMULATION_EQUALITY.label.simulationLearning",
        defaultMessage: "Equal learning simulation"
    }),
    COMPARISON: defineMessage({
        id: "fig.learning.trajectories.TRAJECTORIES_SIMULATION_EQUALITY.label.comparison",
        defaultMessage: "Trajectory of the rich"
    })
};
const legendLookup = {
    CURRENT: defineMessage({
        id: "fig.learning.trajectories.TRAJECTORIES_SIMULATION_EQUALITY.legend.current",
        defaultMessage: "Current learning of poor children (average of 23 low- and lower-middle-income countries)"
    }),
    SIMULATION_ACCESS: defineMessage({
        id: "fig.learning.trajectories.TRAJECTORIES_SIMULATION_EQUALITY.legend.simulationAccess",
        defaultMessage: "Equal access simulation (if poor children got as much schooling as rich children)"
    }),
    SIMULATION_LEARNING: defineMessage({
        id: "fig.learning.trajectories.TRAJECTORIES_SIMULATION_EQUALITY.legend.simulationLearning",
        defaultMessage: "Equal learning simulation (if poor children learned as much per grade as rich children)"
    }),
    COMPARISON: defineMessage({
        id: "fig.learning.trajectories.TRAJECTORIES_SIMULATION_EQUALITY.legend.comparison",
        defaultMessage: "Current learning of rich children (average of 23 low- and lower-middle-income countries)"
    })
};
const lineStyler = (series)=>{
    const strokeWidth = 3;
    if (series === "CURRENT") {
        return {
            strokeWidth,
            stroke: M.q07Palette[6]
        };
    }
    if (series === "SIMULATION_ACCESS") {
        return {
            strokeWidth,
            stroke: M.q08Palette[5],
            strokeDasharray: "6 12"
        };
    }
    if (series === "SIMULATION_LEARNING") {
        return {
            strokeWidth,
            stroke: M.q01Palette[3],
            strokeDasharray: "6 12"
        };
    }
    if (series === "COMPARISON") {
        return {
            strokeWidth,
            stroke: M.q03Palette[7]
        };
    }
    return {
        strokeWidth,
        stroke: M.grayscalePalette[4]
    };
};
export const Data = RiseDataDecoder([
    "value",
    "country",
    "year",
    "series"
], {});
export const Chart = ({ data, renderMode })=>{
    const screenSDown = !useMedia(breakpoints.s);
    const intl = useIntl();
    const { activeSection } = useReadScrolly();
    const activeSectionId = pipe(Steps.decode(O.toNullable(activeSection)), E.getOrElse(()=>Steps.encode("default")));
    const chartPropsAtScrollSteps = {
        CURRENT: {
            lineStyle: (series)=>({
                    ...lineStyler(series),
                    strokeOpacity: series === "CURRENT" ? 1 : 0.5
                }),
            lockActivePoint: data.find((d)=>d.series === "CURRENT" && d.year === 9),
            backgroundSeriesFilter: (series)=>series !== "CURRENT"
        },
        SIMULATION_ACCESS: {
            lineStyle: (series)=>({
                    ...lineStyler(series),
                    strokeOpacity: [
                        "CURRENT",
                        "SIMULATION_ACCESS"
                    ].includes(series.toString()) ? 1 : 0.5
                }),
            lockActivePoint: data.find((d)=>d.series === "CURRENT" && d.year === 9),
            backgroundSeriesFilter: (series)=>![
                    "CURRENT",
                    "SIMULATION_ACCESS"
                ].includes(series.toString())
        },
        SIMULATION_LEARNING: {
            lineStyle: (series)=>({
                    ...lineStyler(series),
                    strokeOpacity: [
                        "CURRENT",
                        "SIMULATION_LEARNING"
                    ].includes(series.toString()) ? 1 : 0.5
                }),
            lockActivePoint: data.find((d)=>d.series === "CURRENT" && d.year === 9),
            backgroundSeriesFilter: (series)=>![
                    "CURRENT",
                    "SIMULATION_LEARNING"
                ].includes(series.toString())
        },
        COMPARISON: {
            lineStyle: (series)=>({
                    ...lineStyler(series),
                    strokeOpacity: [
                        "CURRENT",
                        "COMPARISON"
                    ].includes(series.toString()) ? 1 : 0.5
                }),
            lockActivePoint: data.find((d)=>d.series === "CURRENT" && d.year === 9),
            backgroundSeriesFilter: (series)=>![
                    "CURRENT",
                    "COMPARISON"
                ].includes(series.toString())
        },
        COMPARISON_GROUP: {
            lineStyle: (series)=>({
                    ...lineStyler(series),
                    strokeOpacity: series === "COMPARISON" ? 1 : 0.5
                }),
            lockActivePoint: data.find((d)=>d.series === "COMPARISON" && d.year === 9),
            backgroundSeriesFilter: (series)=>series !== "COMPARISON"
        },
        default: {
            lineStyle: lineStyler
        }
    };
    const seriesAccessor = useCallback((d)=>d.series, []);
    const xAccessor = useCallback((d)=>d.year, []);
    const yAccessor = useCallback((d)=>d.value, []);
    return <TrajectoriesChart height={M.chartHeight.m} dataset={data} seriesAccessor={seriesAccessor} xAccessor={xAccessor} yAccessor={yAccessor} yAxisTitle={intl.formatMessage(yAxisTitleLookup["LIT"])} xAxisTitle={intl.formatMessage(xAxisTitleLookup["AGE"])} formatTooltipLabel={(d)=>intl.formatMessage(seriesLabelsLookup[d.series]) || ""} formatTooltipValue={(d)=>d.value === null ? intl.formatMessage(defineMessage({
            id: "fig.label.data.not.available",
            defaultMessage: "Not Available"
        })) : format(".0%")(d.value)} formatLegendLabel={(series)=>intl.formatMessage(legendLookup[series]) || ""} rules={renderMode === "embed" ? [] : [
        goalRule(intl, "AGE", "LIT")
    ]} showColorLegend={true} legendValues={[
        "COMPARISON",
        "SIMULATION_LEARNING",
        "SIMULATION_ACCESS",
        "CURRENT"
    ]} flipTooltip={screenSDown} {...chartPropsAtScrollSteps[activeSectionId]}/>;
};
export default withFigureIO({
    url: new URL("../data/data_learning_trajectories_fig_TRAJECTORIES_SIMULATION_EQUALITY.json", import.meta.url).href,
    csv: new URL("../data/data_learning_trajectories_fig_TRAJECTORIES_SIMULATION_EQUALITY.zip", import.meta.url).href,
    xlsx: new URL("../data/data_learning_trajectories_fig_TRAJECTORIES_SIMULATION_EQUALITY.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    size: "main",
    datasets
});
